import type { BillView, CheckoutOther, CheckoutView, DeviceView, PaymentSession, PaymentView } from "@vaulted/shared";
import { PAYMENT_METHOD_LABELS, formatCents } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { del, errorMessage, get, post } from "../api";
import { time12 } from "../components/AppointmentDialog";
import { StripeCheckout } from "../components/StripeCheckout";
import { Alert, Badge, Button, Checkbox, Dialog, Field, Loading, MoneyField, Panel, SelectField, cx, formatDateTime } from "../components/ui";
import { useSession } from "../session";
import type { CatalogResponse } from "./Catalog";
import { BackLink, useBackTitle } from "../components/BackLink";

type PayResult = CheckoutView & { session: PaymentSession | null; changeCents: number | null; message: string | null };

/** End of the visit: each guest's bill, tip, and payment. */
export function CheckoutPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  // Other visits (booked separately) added to pay together: kept in the address.
  const [search, setSearch] = useSearchParams();
  const withIds = (search.get("with") ?? "").split(",").filter(Boolean);
  const key = ["checkout", id, withIds.join(",")];
  const data = useQuery({ queryKey: key, queryFn: () => get<CheckoutView>(`/api/appointments/${id}/checkout${withIds.length ? `?with=${withIds.join(",")}` : ""}`) });
  // Changes answer with this visit's own checkout: with others added, load them all again.
  const set = (v: CheckoutView) => (withIds.length ? void data.refetch() : qc.setQueryData(key, v));
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState<string[] | null>(null);
  useBackTitle(data.data ? "Checkout" : null);
  if (data.error) return <Alert>{errorMessage(data.error)}</Alert>;
  const co = data.data;
  if (!co) return <Loading />;
  const open = co.bills.filter((b) => b.status === "open");
  const chosen = (selected ?? open.filter((b) => b.appointmentId === co.appointmentId || withIds.includes(b.appointmentId)).map((b) => b.id)).filter((x) => open.some((b) => b.id === x));
  const main = co.bills.find((b) => b.appointmentId === co.appointmentId)!;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <BackLink to={`/clients/${main.client.id}`} label={main.client.name} className="text-sm text-muted hover:underline" />
        <h1 className="page-title">Checkout</h1>
        <p className="text-ink-soft">
          {new Date(`${main.start.slice(0, 10)}T12:00:00Z`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", timeZone: "UTC" })} · {time12(main.start)}
        </p>
      </div>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_400px]">
        <div className="flex flex-col gap-4">
          <FeesOwed co={co} onChange={set} />
          {co.bills.map((b) => (
            <BillCard
              key={b.id}
              b={b}
              co={co}
              onChange={set}
              picked={chosen.includes(b.id)}
              canPick={open.length > 1 && b.status === "open"}
              onPick={(on) => setSelected(on ? [...chosen, b.id] : chosen.filter((x) => x !== b.id))}
            />
          ))}
          <div>
            <Button variant="quiet" onClick={() => setAdding(true)}>
              + Add another visit
            </Button>
            <span className="ml-2 text-sm text-muted">Booked separately? Check them out together.</span>
          </div>
          {adding && (
            <AddVisitDialog
              id={id}
              skip={co.bills.map((b) => b.appointmentId)}
              onClose={() => setAdding(false)}
              onPick={(other) => {
                setAdding(false);
                setSelected(null);
                setSearch({ with: [...withIds, other].join(",") }, { replace: true });
              }}
            />
          )}
          <PaymentsList co={co} onRefunded={() => void data.refetch()} />
        </div>
        <aside>{chosen.length > 0 ? <PayPanel co={co} billIds={chosen} onDone={(v) => set(v)} refetch={() => void data.refetch()} /> : <Panel className="p-5 text-sm text-ink-soft">{open.length ? "Pick a bill to pay." : "All paid. ✓"}</Panel>}</aside>
      </div>
    </div>
  );
}

function BillCard({ b, co, onChange, picked, canPick, onPick }: { b: BillView; co: CheckoutView; onChange: (v: CheckoutView) => void; picked: boolean; canPick: boolean; onPick: (on: boolean) => void }) {
  const { can } = useSession();
  const [adding, setAdding] = useState(false);
  const [discount, setDiscount] = useState(false);
  const [customTip, setCustomTip] = useState(false);
  const act = useMutation({ mutationFn: (fn: () => Promise<CheckoutView>) => fn(), onSuccess: onChange });
  const open = b.status === "open";
  const tip = (cents: number) => act.mutate(() => post<CheckoutView>(`/api/bills/${b.id}/tip`, { tipCents: cents }));
  return (
    <Panel className={cx("p-5", picked && "ring-2 ring-brand")}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {canPick && <input type="checkbox" className="size-4 accent-[var(--color-brand)]" checked={picked} onChange={(e) => onPick(e.target.checked)} aria-label={`Pay ${b.client.name}'s bill`} />}
        <h2 className="text-base font-semibold">{b.client.name}</h2>
        <span className="text-sm text-muted">{time12(b.start)}</span>
        {b.status === "paid" ? <Badge tone="ok">Paid</Badge> : <Badge tone="wait">Open</Badge>}
      </div>
      <table className="table-base">
        <tbody>
          {b.lines.map((l) => (
            <tr key={l.id}>
              <td>
                {l.quantity > 1 && `${l.quantity} × `}
                {l.name}
                {l.source === "chart" && <span className="ml-2 text-xs font-semibold text-wait">Added on chart</span>}
                {l.source === "added" && open && (
                  <button className="ml-2 text-xs text-muted underline" onClick={() => act.mutate(() => del<CheckoutView>(`/api/bills/${b.id}/lines/${l.id}`))}>
                    Remove
                  </button>
                )}
                {l.notCharted && <div className="text-xs font-semibold text-wait">Not on the signed chart. The nurse will be asked to update it.</div>}
              </td>
              <td className="text-right tabular-nums">
                {l.coveredBy ? (
                  <>
                    <span className="mr-2 text-muted line-through">{formatCents(l.amountCents)}</span>
                    {formatCents(0)}
                  </>
                ) : (
                  formatCents(l.amountCents)
                )}
              </td>
            </tr>
          ))}
          {b.lines.some((l) => l.coveredBy) && (
            <tr>
              <td colSpan={2} className="text-xs font-semibold text-ok">
                Covered by {[...new Set(b.lines.map((l) => l.coveredBy).filter(Boolean))].join(", ")} (voucher used when the bill is closed)
              </td>
            </tr>
          )}
          {b.lines
            .filter((l) => l.planDiscountCents > 0)
            .map((l) => (
              <tr key={`d-${l.id}`}>
                <td className={b.discountApplied === "plan" ? "text-ok" : "text-muted line-through"}>
                  {l.planDiscountLabel} <span className="text-xs">({l.name})</span>
                </td>
                <td className={cx("text-right tabular-nums", b.discountApplied === "plan" ? "text-ok" : "text-muted line-through")}>−{formatCents(l.planDiscountCents)}</td>
              </tr>
            ))}
          {b.planDiscountCents > 0 && b.discountApplied === "manual" && (
            <tr>
              <td colSpan={2} className="text-xs text-muted">
                Member pricing isn't added on top: the discount below is larger.
              </td>
            </tr>
          )}
          {b.planDiscountOffReason && (
            <tr>
              <td colSpan={2} className="text-xs text-muted">
                Member pricing off: {b.planDiscountOffReason}
              </td>
            </tr>
          )}
          {b.discountCents > 0 && (
            <tr>
              <td className={b.discountApplied === "manual" ? "text-ok" : "text-muted line-through"}>Discount{b.discountReason && <span className="text-xs text-muted"> ({b.discountReason})</span>}</td>
              <td className={cx("text-right tabular-nums", b.discountApplied === "manual" ? "text-ok" : "text-muted line-through")}>−{formatCents(b.discountCents)}</td>
            </tr>
          )}
          {b.discountCents > 0 && b.discountApplied === "plan" && (
            <tr>
              <td colSpan={2} className="text-xs text-muted">
                Not added on top: member pricing is larger.
              </td>
            </tr>
          )}
          {b.depositCreditCents > 0 && (
            <tr>
              <td className="text-ok">Deposit paid</td>
              <td className="text-right text-ok tabular-nums">−{formatCents(b.depositCreditCents)}</td>
            </tr>
          )}
          <tr>
            <td>Tip (for the nurse)</td>
            <td className="text-right tabular-nums">{formatCents(b.tipCents)}</td>
          </tr>
          <tr className="font-semibold">
            <td>Total</td>
            <td className="text-right tabular-nums">{formatCents(b.totalCents)}</td>
          </tr>
          {b.paidCents > 0 && (
            <tr>
              <td>Paid</td>
              <td className="text-right tabular-nums">{formatCents(b.paidCents)}</td>
            </tr>
          )}
          {open && b.paidCents > 0 && (
            <tr className="font-semibold">
              <td>Balance</td>
              <td className="text-right tabular-nums">{formatCents(b.balanceCents)}</td>
            </tr>
          )}
        </tbody>
      </table>
      {open && (
        <div className="mt-4 flex flex-col gap-3">
          <div>
            <p className="mb-1.5 text-sm font-semibold text-ink-soft">Tip</p>
            <div className="flex flex-wrap gap-2">
              {co.tipPercents.map((p) => {
                const cents = Math.round((b.tipBaseCents * p) / 100);
                return (
                  <TipChip key={p} on={b.tipCents === cents && cents > 0} onClick={() => tip(cents)}>
                    {p}% · {formatCents(cents)}
                  </TipChip>
                );
              })}
              <TipChip on={customTip} onClick={() => setCustomTip(true)}>
                Custom
              </TipChip>
              <TipChip on={b.tipCents === 0} onClick={() => (setCustomTip(false), tip(0))}>
                No tip
              </TipChip>
            </div>
            {customTip && <MoneyField className="mt-2 max-w-48" label="Custom tip" cents={b.tipCents} onChange={(c) => c != null && tip(c)} />}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="quiet" onClick={() => setAdding(true)}>
              + Add item
            </Button>
            {can("prices.override") && (
              <Button variant="quiet" onClick={() => setDiscount(true)}>
                Discount…
              </Button>
            )}
            {can("prices.override") && (b.planDiscountCents > 0 || b.planDiscountOffReason) && (
              <Button
                variant="quiet"
                onClick={() => {
                  if (b.planDiscountOffReason) return act.mutate(() => post<CheckoutView>(`/api/bills/${b.id}/member-pricing`, { on: true }));
                  const reason = prompt("Why take member pricing off this bill?");
                  if (reason) act.mutate(() => post<CheckoutView>(`/api/bills/${b.id}/member-pricing`, { on: false, reason }));
                }}
              >
                {b.planDiscountOffReason ? "Put member pricing back" : "Remove member pricing…"}
              </Button>
            )}
          </div>
        </div>
      )}
      {act.error && <Alert>{errorMessage(act.error)}</Alert>}
      {adding && <AddItemDialog billId={b.id} onClose={() => setAdding(false)} onDone={(v) => (onChange(v), setAdding(false))} />}
      {discount && <DiscountDialog b={b} onClose={() => setDiscount(false)} onDone={(v) => (onChange(v), setDiscount(false))} />}
    </Panel>
  );
}

function TipChip({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} className={cx("min-h-11 rounded-full border px-4 py-2 text-sm font-medium", on ? "border-brand bg-brand text-white" : "border-line bg-white hover:border-ink-soft")}>
      {children}
    </button>
  );
}

function AddItemDialog({ billId, onClose, onDone }: { billId: string; onClose: () => void; onDone: (v: CheckoutView) => void }) {
  const catalog = useQuery({ queryKey: ["catalog"], queryFn: () => get<CatalogResponse>("/api/catalog") });
  const [itemId, setItemId] = useState("");
  const [qty, setQty] = useState(1);
  const add = useMutation({ mutationFn: () => post<CheckoutView>(`/api/bills/${billId}/lines`, { catalogItemId: itemId, quantity: qty }), onSuccess: onDone });
  const items = (catalog.data?.items ?? []).filter((i) => i.status === "active" && (i.type === "product" || i.type === "add_on"));
  return (
    <Dialog
      title="Add an item"
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!itemId} busy={add.isPending} onClick={() => add.mutate()}>
            Add
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <SelectField label="Product or add-on" value={itemId} onChange={(e) => setItemId(e.target.value)}>
          <option value="">Choose…</option>
          {items.map((i) => (
            <option key={i.id} value={i.id}>
              {i.name} · {formatCents(i.priceCents)}
              {i.type === "product" ? " (product)" : ""}
            </option>
          ))}
        </SelectField>
        <Field label="Quantity" type="number" min={1} max={50} className="max-w-32" value={qty} onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))} />
        <p className="text-sm text-muted">Items the nurse gives during the visit are added from the chart automatically.</p>
        {add.error && <Alert>{errorMessage(add.error)}</Alert>}
      </div>
    </Dialog>
  );
}

function DiscountDialog({ b, onClose, onDone }: { b: BillView; onClose: () => void; onDone: (v: CheckoutView) => void }) {
  const [kind, setKind] = useState<"percent" | "amount">("percent");
  const [value, setValue] = useState(b.discountCents ? (b.discountCents / 100).toFixed(2) : "");
  const [reason, setReason] = useState(b.discountReason ?? "");
  const save = useMutation({
    mutationFn: () => post<CheckoutView>(`/api/bills/${b.id}/discount`, { kind, value: kind === "amount" ? Math.round(Number(value) * 100) : Number(value), reason }),
    onSuccess: onDone,
  });
  return (
    <Dialog
      title="Discount"
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
            Apply
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="flex gap-2">
          <SelectField label="Type" value={kind} onChange={(e) => setKind(e.target.value as "percent" | "amount")}>
            <option value="percent">Percent</option>
            <option value="amount">Dollars</option>
          </SelectField>
          <Field label={kind === "percent" ? "Percent off" : "Dollars off"} inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} />
        </div>
        <Field label="Reason" required value={reason} onChange={(e) => setReason(e.target.value)} hint="Logged with the bill. Use 0 to remove a discount." />
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
      </div>
    </Dialog>
  );
}

function PayPanel({ co, billIds, onDone, refetch }: { co: CheckoutView; billIds: string[]; onDone: (v: CheckoutView) => void; refetch: () => void }) {
  const bills = co.bills.filter((b) => billIds.includes(b.id));
  const total = bills.reduce((s, b) => s + b.balanceCents, 0);
  const cards = [...new Map(Object.values(co.cards).flat().map((c) => [c.id, c])).values()];
  const fallback = cards.length ? "card_on_file" : "card";
  const [method, setMethod] = useState<CheckoutView["startsOn"]>(fallback);
  // Account credit (the first bill's client's, the one paying) comes off first, unless staff untick it.
  const creditCents = co.credit[bills[0]?.client.id ?? ""] ?? 0;
  const [useCredit, setUseCredit] = useState(true);
  const creditUse = useCredit ? Math.min(creditCents, total) : 0;
  const creditCovers = creditUse > 0 && creditUse >= total;
  const owing = (v: CheckoutView) => v.bills.filter((b) => billIds.includes(b.id)).reduce((s, b) => s + b.balanceCents, 0);
  const applyCredit = async (): Promise<PayResult | null> => {
    if (creditUse <= 0) return null;
    const r = await post<PayResult>("/api/checkout/pay", { billIds, method: "account_credit", savedCardId: null, readerId: null, cashReceivedCents: null });
    onDone(r);
    return r;
  };
  // Start on the clinic's choice once it's known to be available here (iPads load a moment later).
  const [started, setStarted] = useState(false);
  // Clinic iPads the client can check out on (tip and pay themselves).
  const devices = useQuery({ queryKey: ["devices"], queryFn: () => get<DeviceView[]>("/api/devices"), refetchInterval: 5000 });
  const ipads = (devices.data ?? []).filter((d) => d.paired);
  const onIpad = ipads.find((d) => d.checkoutFor?.appointmentId && bills.some((b) => b.appointmentId === d.checkoutFor!.appointmentId));
  const [deviceId, setDeviceId] = useState("");
  const sendToIpad = useMutation({
    mutationFn: async () => (await applyCredit(), post<CheckoutView>("/api/checkout/send-to-device", { deviceId: deviceId || ipads[0]?.id, billIds, readerId: co.readerEnabled && readerId ? readerId : null })),
    onSuccess: (v) => (onDone(v), void devices.refetch()),
  });
  const takeBack = useMutation({ mutationFn: (id: string) => post(`/api/devices/${id}/clear`, {}), onSuccess: () => void devices.refetch() });
  // On the iPad: follow along until it's paid.
  useQuery({ queryKey: ["checkout-ipad", co.appointmentId], queryFn: async () => (refetch(), null), enabled: !!onIpad, refetchInterval: 3000 });
  const [cardId, setCardId] = useState(cards[0]?.id ?? "");
  const [readerId, setReaderId] = useState(co.readers[0]?.id ?? "");
  // Paying on the reader with no card on file: keep the card for next time (ticked unless unticked).
  const payerHasCard = (co.cards[bills[0]?.client.id ?? ""] ?? []).length > 0;
  const [saveCard, setSaveCard] = useState(true);
  const [received, setReceived] = useState<number | null>(null);
  const [linkTo, setLinkTo] = useState(co.linkTo ?? "");
  const [result, setResult] = useState<PayResult | null>(null);
  const [linkSent, setLinkSent] = useState<string | null>(null);
  const pay = useMutation({
    mutationFn: async () => {
      const paid = await applyCredit();
      if (paid && owing(paid) <= 0) return paid;
      return post<PayResult>("/api/checkout/pay", { billIds, method, savedCardId: method === "card_on_file" ? cardId : null, readerId: method === "card_present" ? readerId : null, cashReceivedCents: method === "cash" ? received : null, saveCard: method === "card_present" && !payerHasCard && saveCard });
    },
    onSuccess: (r) => {
      setResult(r);
      if (!r.session) onDone(r);
    },
  });
  const link = useMutation({
    mutationFn: async () => (await applyCredit(), post<{ link: string; sent: boolean }>("/api/checkout/pay-link", { billIds, channel: linkTo.includes("@") ? "email" : "sms", to: linkTo })),
    onSuccess: (r) => (setLinkSent(r.sent ? `Sent to ${linkTo}.` : `Couldn't send it; copy the link instead: ${r.link}`), refetch()),
  });
  const close = useMutation({ mutationFn: () => post<CheckoutView>("/api/checkout/close", { billIds }), onSuccess: onDone });
  // The latest view: on the reader the client may switch (card on file → tap), which starts a new payment.
  const pendingReader = co.payments.find((p) => p.method === "card_present" && p.status === "pending");
  const sentNow = result?.payments.find((p) => p.method === "card_present" && p.status === "pending");
  // The card just sent was declined on the reader: say why, and that another card can be sent.
  const declined = sentNow ? co.payments.find((p) => p.id === sentNow.id && p.status === "failed") : undefined;
  const message = declined
    ? `${declined.failureMessage ?? "The card was declined."} Send to the reader again to try another card.`
    : !sentNow ? result?.message : !pendingReader ? null : pendingReader.id === sentNow.id ? result?.message : "Waiting on the reader: the client taps or inserts a card.";
  // They'd rather pay another way: stop the reader and drop the waiting payment.
  const [cancelNote, setCancelNote] = useState<string | null>(null);
  const cancelReader = useMutation({
    mutationFn: (id: string) => post<CheckoutView & { message: string | null }>(`/api/checkout/payments/${id}/cancel`, {}),
    onSuccess: (v) => (setResult(null), setCancelNote(v.message), onDone(v)),
  });
  // No card taken after 45 seconds: the reader may be stuck on its card screen. Sending again replaces it there.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!pendingReader) return;
    const t = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(t);
  }, [pendingReader]);
  const stuck = !!pendingReader && method === "card_present" && now - Date.parse(pendingReader.at) > 45_000;
  // Waiting on the reader: check until it's paid.
  useQuery({ queryKey: ["checkout-poll", co.appointmentId], queryFn: async () => (refetch(), null), enabled: !!pendingReader, refetchInterval: 2000 });

  const methods: { key: typeof method; label: string; show: boolean }[] = [
    { key: "card_on_file", label: "Card on file", show: cards.length > 0 },
    { key: "card", label: "New card", show: true },
    { key: "card_present", label: "Card reader", show: co.readerEnabled && co.readers.length > 0 },
    { key: "cash", label: "Cash", show: true },
    { key: "link", label: total <= 0 ? "Text a tip link" : "Text a pay link", show: true },
    { key: "ipad", label: "On the iPad", show: ipads.length > 0 },
  ];
  // Covered by a voucher (nothing owed): close it, or ask for a tip on the reader, the iPad, or by link.
  const covered = total <= 0;
  const tipWays: (typeof method)[] = ["card_present", "link", "ipad"];
  const preferred = methods.find((m) => m.key === co.startsOn);
  useEffect(() => {
    if (started || !preferred) return;
    if (preferred.show) (setMethod(preferred.key), setStarted(true));
    else if (co.startsOn !== "ipad" || devices.isFetched) setStarted(true);
  }, [started, preferred, co.startsOn, devices.isFetched]);
  return (
    <Panel className="flex flex-col gap-4 p-5">
      <div className="flex items-baseline justify-between">
        <h2 className="text-base font-semibold">Pay</h2>
        <span className="text-2xl font-semibold tabular-nums">{formatCents(total)}</span>
      </div>
      {creditCents > 0 && total > 0 && (
        <div className="flex flex-col gap-1 rounded-[var(--radius-control)] bg-mist/60 px-3 py-2">
          <Checkbox label={`Use their account credit (${formatCents(creditCents)})`} checked={useCredit} onChange={setUseCredit} />
          {useCredit && (
            <p className="text-sm text-ink-soft">
              {creditCovers ? `Pays all ${formatCents(total)} (${formatCents(creditCents - total)} left on their account).` : `${formatCents(creditUse)} comes off first; ${formatCents(total - creditUse)} is left to pay below.`}
            </p>
          )}
        </div>
      )}
      {creditCovers ? (
        <Button variant="primary" className="py-3" busy={pay.isPending} onClick={() => pay.mutate()}>
          Pay {formatCents(total)} from account credit
        </Button>
      ) : (
      <>
      {covered && (
        <>
          <p className="text-sm text-ink-soft">Nothing is owed{bills.some((b) => b.coveredCents > 0) ? ": a voucher covers it" : ""}. Close it, or ask the client for a tip on:</p>
          <Button variant={tipWays.includes(method) ? "secondary" : "primary"} className="py-3" busy={close.isPending} onClick={() => close.mutate()}>
            Close {bills.length > 1 ? "bills" : "bill"} (no tip)
          </Button>
          {close.error && <Alert>{errorMessage(close.error)}</Alert>}
        </>
      )}
      <div className="flex flex-wrap gap-2">
        {methods
          .filter((m) => m.show && (!covered || tipWays.includes(m.key)))
          .map((m) => (
            <TipChip key={m.key} on={method === m.key} onClick={() => (setMethod(m.key), setResult(null), pay.reset())}>
              {m.label}
            </TipChip>
          ))}
      </div>
      {method === "card_on_file" && (
        <SelectField label="Card" value={cardId} onChange={(e) => setCardId(e.target.value)}>
          {cards.map((c) => (
            <option key={c.id} value={c.id}>
              {c.brand.toUpperCase()} •••• {c.last4}
              {c.expMonth ? ` (exp ${c.expMonth}/${String(c.expYear).slice(-2)})` : ""}
            </option>
          ))}
        </SelectField>
      )}
      {method === "card_present" && (
        <SelectField label="Reader" value={readerId} onChange={(e) => setReaderId(e.target.value)}>
          {co.readers.map((r) => (
            <option key={r.id} value={r.id}>
              {r.label}
            </option>
          ))}
        </SelectField>
      )}
      {method === "card_present" && !covered && !payerHasCard && <Checkbox label="Save this card for faster checkout" checked={saveCard} onChange={setSaveCard} />}
      {method === "ipad" && (
        <>
          <p className="text-sm text-ink-soft">The client sees the bill on the iPad, adds a tip, and pays with their card on file, the card reader, or a new card. This screen follows along.</p>
          {ipads.length > 1 && (
            <SelectField label="iPad" value={deviceId || ipads[0]!.id} onChange={(e) => setDeviceId(e.target.value)}>
              {ipads.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                  {d.online ? "" : " (offline)"}
                </option>
              ))}
            </SelectField>
          )}
          {co.readerEnabled && co.readers.length > 1 && (
            <SelectField label="Card reader there" value={readerId} onChange={(e) => setReaderId(e.target.value)}>
              {co.readers.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
            </SelectField>
          )}
          {onIpad ? (
            <div className="flex flex-wrap items-center gap-2">
              <Alert tone="info">On {onIpad.name}: waiting for the client.</Alert>
              <Button variant="quiet" busy={takeBack.isPending} onClick={() => takeBack.mutate(onIpad.id)}>
                Take it back
              </Button>
            </div>
          ) : (
            <Button variant="primary" className="py-3" busy={sendToIpad.isPending} onClick={() => sendToIpad.mutate()}>
              Send to {ipads.length === 1 ? ipads[0]!.name : "the iPad"}
            </Button>
          )}
          {sendToIpad.error && <Alert>{errorMessage(sendToIpad.error)}</Alert>}
        </>
      )}
      {method === "cash" && <MoneyField label="Cash received (optional)" optional cents={received} onChange={setReceived} hint="For working out change." />}
      {method === "ipad" ? null : method === "link" ? (
        <>
          <Field label="Send to (mobile or email)" placeholder="(405) 555-0100 or parent@example.com" value={linkTo} onChange={(e) => setLinkTo(e.target.value)} hint="For someone paying later, like a parent. Staff never handle the card." />
          {linkSent && <Alert tone="ok">{linkSent}</Alert>}
          {link.error && <Alert>{errorMessage(link.error)}</Alert>}
          <Button variant="primary" busy={link.isPending} disabled={linkTo.length < 3} onClick={() => link.mutate()}>
            {covered ? "Send tip link" : "Send pay link"}
          </Button>
        </>
      ) : covered ? (
        method === "card_present" && (
          <Button variant="primary" className="py-3" busy={pay.isPending} onClick={() => pay.mutate()}>
            Ask for a tip on the reader
          </Button>
        )
      ) : result?.session ? (
        <StripeCheckout session={result.session} onComplete={() => (setResult(null), refetch())} />
      ) : (
        <Button variant="primary" className="py-3" busy={pay.isPending} disabled={total <= 0} onClick={() => pay.mutate()}>
          {method === "card" ? "Enter a new card" : method === "card_present" ? "Send to reader" : `Charge ${formatCents(total - creditUse)}`}
        </Button>
      )}
      </>
      )}
      {pay.error && <Alert>{errorMessage(pay.error)}</Alert>}
      {message && <Alert tone={pendingReader ? "info" : "danger"}>{message}</Alert>}
      {stuck && (
        <>
          <Alert tone="info">Still waiting on the reader. If it won't take the card, send the payment to it again.</Alert>
          <Button variant="primary" busy={pay.isPending} onClick={() => (setCancelNote(null), pay.mutate())}>
            Send again
          </Button>
        </>
      )}
      {pendingReader && (
        <Button variant="quiet" busy={cancelReader.isPending} onClick={() => cancelReader.mutate(pendingReader.id)}>
          Cancel and pay another way
        </Button>
      )}
      {cancelReader.error && <Alert>{errorMessage(cancelReader.error)}</Alert>}
      {cancelNote && !pendingReader && <Alert>{cancelNote}</Alert>}
      {pendingReader && !co.publishableKey && (
        <Button variant="quiet" onClick={() => void post(`/api/dev/payments/${pendingReader.id}/tap`).then(refetch)}>
          Simulate tap (test mode)
        </Button>
      )}
      {result?.changeCents != null && result.changeCents > 0 && <Alert tone="ok">Change due: {formatCents(result.changeCents)}</Alert>}
    </Panel>
  );
}

function PaymentsList({ co, onRefunded }: { co: CheckoutView; onRefunded: () => void }) {
  const { can } = useSession();
  const [refunding, setRefunding] = useState<PaymentView | null>(null);
  if (!co.payments.length) return null;
  return (
    <Panel className="p-5">
      <h2 className="mb-2 text-base font-semibold">Payments</h2>
      <ul className="flex flex-col divide-y divide-mist text-sm">
        {co.payments.map((p) => (
          <li key={p.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
            <span className="w-40 text-muted">{formatDateTime(p.at)}</span>
            <span className="min-w-0 flex-1">
              <span className="font-semibold">{p.kind === "refund" ? "Refund" : p.kind === "deposit" ? "Deposit" : p.kind === "fee" ? `Fee (${p.reason})` : "Payment"}</span> · {PAYMENT_METHOD_LABELS[p.method]}
              {p.cardLast4 && ` •••• ${p.cardLast4}`}
              {p.payer && <span className="text-muted"> · {p.payer}</span>}
              {p.failureMessage && <span className="block text-xs text-danger">{p.failureMessage}</span>}
            </span>
            {p.kind === "refund" || (p.status === "succeeded" && p.refundedCents >= p.amountCents) ? (
              <Badge tone="off">Refunded</Badge>
            ) : (
              <Badge tone={p.status === "succeeded" ? "ok" : p.status === "failed" ? "off" : "wait"}>{p.status === "succeeded" ? "Paid" : p.status === "authorized" ? "Held" : p.status[0]!.toUpperCase() + p.status.slice(1)}</Badge>
            )}
            <span className="w-20 text-right font-semibold tabular-nums">
              {p.kind === "refund" ? "−" : ""}
              {formatCents(p.amountCents)}
            </span>
            {can("payments.refund") && p.kind !== "refund" && p.status === "succeeded" && p.refundedCents < p.amountCents && (
              <button className="text-xs text-muted underline" onClick={() => setRefunding(p)}>
                Refund…
              </button>
            )}
          </li>
        ))}
      </ul>
      {refunding && <RefundDialog p={refunding} onClose={() => setRefunding(null)} onDone={() => (setRefunding(null), onRefunded())} />}
    </Panel>
  );
}

function RefundDialog({ p, onClose, onDone }: { p: PaymentView; onClose: () => void; onDone: () => void }) {
  // Always the whole payment: the bill reopens, and a discount then re-bills it for less.
  const cents = p.amountCents - p.refundedCents;
  const [reason, setReason] = useState("");
  // Paid with credit: it goes back as credit. Otherwise it can be given as credit instead of money back.
  const [toCredit, setToCredit] = useState(p.method === "account_credit");
  const refund = useMutation({ mutationFn: () => post(`/api/payments/${p.id}/refund`, { amountCents: cents, reason, toCredit }), onSuccess: onDone });
  return (
    <Dialog
      title="Refund"
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" busy={refund.isPending} disabled={!cents || !reason.trim()} onClick={() => refund.mutate()}>
            Refund {cents ? formatCents(cents) : ""}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-ink-soft">
          {toCredit ? "It goes on their account as credit (no money goes back)." : p.method === "cash" ? "Give the cash back; this records it." : "The money goes back to the card it came from."} The bill opens again, so the visit can be checked out again (add a discount first to charge less).
        </p>
        {p.method !== "account_credit" && <Checkbox label="Give it as account credit instead" checked={toCredit} onChange={setToCredit} />}
        <Field label="Reason" required value={reason} onChange={(e) => setReason(e.target.value)} />
        {refund.error && <Alert>{errorMessage(refund.error)}</Alert>}
      </div>
    </Dialog>
  );
}

/** Today's other unpaid visits (booked separately), to add to this checkout. */
function AddVisitDialog({ id, skip, onClose, onPick }: { id: string; skip: string[]; onClose: () => void; onPick: (appointmentId: string) => void }) {
  const others = useQuery({ queryKey: ["checkout-others", id], queryFn: () => get<CheckoutOther[]>(`/api/appointments/${id}/checkout/others`) });
  const list = (others.data ?? []).filter((o) => !skip.includes(o.appointmentId));
  return (
    <Dialog title="Add another visit" onClose={onClose}>
      {others.isLoading ? (
        <Loading />
      ) : others.error ? (
        <Alert>{errorMessage(others.error)}</Alert>
      ) : list.length ? (
        <ul className="flex flex-col gap-2">
          {list.map((o) => (
            <li key={o.appointmentId}>
              <button type="button" onClick={() => onPick(o.appointmentId)} className="flex w-full items-center justify-between gap-3 rounded-[var(--radius-control)] border border-line bg-white px-3 py-2 text-left hover:border-brand">
                <span>
                  <span className="block font-semibold">{o.name}</span>
                  <span className="text-sm text-ink-soft">{o.serviceName}</span>
                </span>
                <span className="text-sm tabular-nums text-muted">{time12(o.time)}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-ink-soft">No other unpaid visits today.</p>
      )}
    </Dialog>
  );
}

/** Fees owed from before (a no-show or late cancel with no card): staff add one to a bill or waive it. Nothing is added on its own. */
function FeesOwed({ co, onChange }: { co: CheckoutView; onChange: (v: CheckoutView) => void }) {
  const qc = useQueryClient();
  const bill = useMutation({ mutationFn: (v: { id: string; billId: string }) => post<CheckoutView>(`/api/fees/${v.id}/bill`, { billId: v.billId }), onSuccess: onChange });
  const waive = useMutation({ mutationFn: (id: string) => post(`/api/fees/${id}/waive`, {}), onSuccess: () => void qc.invalidateQueries({ queryKey: ["checkout"] }) });
  if (!co.feesOwed.length) return null;
  const open = co.bills.filter((b) => b.status === "open");
  return (
    <Alert tone="info">
      <div className="flex flex-col gap-2">
        {co.feesOwed.map((f) => {
          const target = open.find((b) => b.client.id === f.clientId) ?? open[0];
          const who = co.bills.find((b) => b.client.id === f.clientId)?.client.name;
          return (
            <div key={f.paymentId} className="flex flex-wrap items-center gap-2">
              <span className="font-semibold">
                Fee owed{who && co.bills.length > 1 ? ` (${who})` : ""}: {formatCents(f.amountCents)} · {f.reason}, {new Date(`${f.date}T12:00:00Z`).toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" })}
              </span>
              {target && (
                <Button variant="quiet" busy={bill.isPending && bill.variables?.id === f.paymentId} onClick={() => bill.mutate({ id: f.paymentId, billId: target.id })}>
                  Add to bill
                </Button>
              )}
              <Button variant="quiet" busy={waive.isPending && waive.variables === f.paymentId} onClick={() => window.confirm("Waive this fee? It won't be flagged again.") && waive.mutate(f.paymentId)}>
                Waive
              </Button>
            </div>
          );
        })}
        {(bill.error || waive.error) && <span className="text-danger">{errorMessage(bill.error ?? waive.error)}</span>}
      </div>
    </Alert>
  );
}
